import { cn, Popover, PopoverContent } from '@bridebook/aura';
import * as PopoverPrimitive from '@radix-ui/react-popover';
import { ComponentProps, createContext, PropsWithChildren, useContext } from 'react';
import {
  infoBubbleArrowVariants,
  InfoBubbleContent,
  InfoBubbleHeader,
  InfoBubbleText,
  InfoBubbleVariant,
} from '../../tooltip';

type SimplePopoverVariant = InfoBubbleVariant;

const SimplePopoverContext = createContext<SimplePopoverVariant>('dark');

export const useSimplePopoverContext = () => {
  const context = useContext(SimplePopoverContext);
  if (!context) {
    throw new Error('useSimplePopoverContext must be used within a SimplePopoverProvider');
  }
  return context;
};

type SimplePopoverProps = ComponentProps<typeof Popover> & {
  variant?: SimplePopoverVariant;
  children: React.ReactNode;
};

export const SimplePopover = ({ variant = 'dark', ...props }: SimplePopoverProps) => (
  <SimplePopoverContext.Provider value={variant}>
    <Popover {...props} />
  </SimplePopoverContext.Provider>
);

export const SimplePopoverHeader = ({ children, ...props }: PropsWithChildren) => {
  const variant = useSimplePopoverContext();

  return (
    <InfoBubbleHeader variant={variant} {...props}>
      {children}
    </InfoBubbleHeader>
  );
};

export const SimplePopoverText = ({ children, ...props }: PropsWithChildren) => {
  const variant = useSimplePopoverContext();
  return (
    <InfoBubbleText variant={variant} {...props}>
      {children}
    </InfoBubbleText>
  );
};

export const SimplePopoverContent = ({
  className,
  sideOffset = 0,
  children,
  ...props
}: ComponentProps<typeof PopoverContent>) => {
  const variant = useSimplePopoverContext();
  return (
    <PopoverContent
      align="center"
      variant="pure"
      className={className}
      sideOffset={sideOffset}
      {...props}>
      <InfoBubbleContent variant={variant}>{children}</InfoBubbleContent>
      <PopoverPrimitive.Arrow className={cn(infoBubbleArrowVariants({ variant }))} />
    </PopoverContent>
  );
};
